/* eslint-disable no-unused-vars */
import { _, helpers, mui, React, ts, ui } from '_core';

import { useMemo } from 'react';

import { prepareCovariancesChart, prepareReturnsAndWeightsChart } from './helpers';

type ChartProps = {
  id: string;
  readOnly: boolean;
  widgetKey: string;
  fullScreen: boolean;
  loadWidgetData: (_payload: ts.types.widgets.WidgetGetDataParams) => Promise<ts.types.widgets.WidgetGetDataResponse>;
  definition: ts.types.signal.OptimizedModel;
  optimizedCovariance: string;
  optimizedAlphaData: string;
  title: string;
};

enum TabsEnum {
  FACTOR_RETURNS_AND_WEIGHTS = 'Factor Returns and Weights',
  FACTOR_COVARIANCE = 'Factor Covariance',
}

const Chart = ({
  id,
  readOnly,
  fullScreen,
  loadWidgetData,
  definition,
  optimizedAlphaData,
  optimizedCovariance,
  title,
}: ChartProps) => {
  const [data, setData] = React.useState<ts.types.widgets.TableData>(null);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<ts.types.common.ApiError>(null);

  const [selectedTab, setSelectedTab] = React.useState<TabsEnum>(TabsEnum.FACTOR_RETURNS_AND_WEIGHTS);

  const loadData = async (tab: TabsEnum) => {
    try {
      setLoading(true);
      setData(null);
      const response = await loadWidgetData({
        data: {
          file: tab == TabsEnum.FACTOR_RETURNS_AND_WEIGHTS ? optimizedAlphaData : optimizedCovariance,
        },
      });

      if (response) {
        setData(response.data);
      }
    } catch (err) {
      setError(err as ts.types.common.ApiError);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (optimizedAlphaData && optimizedCovariance) loadData(selectedTab);
    return () => setData(null);
  }, [optimizedAlphaData, optimizedCovariance, selectedTab]);

  const getTabs = () => {
    return (
      <mui.core.Tabs
        indicatorColor="primary"
        textColor="primary"
        value={selectedTab}
        onChange={(_e, newTab) => setSelectedTab(newTab)}
      >
        <mui.core.Tab label={TabsEnum.FACTOR_RETURNS_AND_WEIGHTS} value={TabsEnum.FACTOR_RETURNS_AND_WEIGHTS} />
        <mui.core.Tab label={TabsEnum.FACTOR_COVARIANCE} value={TabsEnum.FACTOR_COVARIANCE} />
      </mui.core.Tabs>
    );
  };

  const formattedData = useMemo(() => {
    if (_.isNil(data) || loading) return null;
    if (selectedTab == TabsEnum.FACTOR_COVARIANCE) return prepareCovariancesChart(data, definition.signals);
    return prepareReturnsAndWeightsChart(data, definition.signals);
  }, [data]);

  return (
    <mui.core.Box display="flex" flexDirection="column" style={{ height: '100%' }}>
      {getTabs()}
      <mui.core.Box p={3} flex="1">
        {loading && (
          <mui.core.Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <mui.core.CircularProgress />
          </mui.core.Box>
        )}
        {!loading && selectedTab == TabsEnum.FACTOR_RETURNS_AND_WEIGHTS && (
          <ui.LayeredChart
            id={id}
            data={formattedData}
            xAxisField="variable"
            leftTitle="Returns"
            rightTitle="Weights"
            outerBar="true_returns"
            outerBarTitle="Realized Returns"
            innerBar="returns"
            innerBarTitle="Forecast Returns"
            line="weights"
            lineTitle="Weights"
            alert={error ? { type: 'error', message: helpers.parseApiError(error) } : undefined}
            exportTitle={title}
            fullScreen={fullScreen}
            readOnly={readOnly}
          />
        )}
        {!loading && selectedTab == TabsEnum.FACTOR_COVARIANCE && (
          <ui.HeatmapChart
            id={id}
            data={formattedData}
            xAxisField="factor_x"
            yAxisField="factor_y"
            alert={error ? { type: 'error', message: helpers.parseApiError(error) } : undefined}
            exportTitle={title}
            fullScreen={fullScreen}
            readOnly={readOnly}
            rotation={45}
          />
        )}
      </mui.core.Box>
    </mui.core.Box>
  );
};

export default Chart;
